import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Download,
  ExternalLink,
  FileText,
  Image as ImageIcon,
  Plus,
  Search,
  Sparkles,
  Trash2,
  Upload,
  Video,
} from 'lucide-react';
import { GroupMemberProfile } from '../types';

export interface MediaItem {
  id: string;
  ownerUserId: string;
  title: string;
  description: string;
  category: 'photo' | 'video' | 'tactics' | 'document';
  fileUrl: string;
  thumbnailUrl?: string;
  fileSize?: string;
  authorName: string;
  authorRole: string;
  createdAt: string;
  downloadsCount: number;
  storageKind?: 'local' | 'external';
}

interface MediaLibraryViewProps {
  activeMember: GroupMemberProfile;
  isOrganizer: boolean;
}

const categoryLabel: Record<MediaItem['category'], string> = {
  photo: 'Photos',
  video: 'Match Highlights',
  tactics: 'Tactics & Playbooks',
  document: 'Documents & Files',
};

const formatWhen = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
  return date.toLocaleDateString();
};

export const MediaLibraryView: React.FC<MediaLibraryViewProps> = ({ activeMember, isOrganizer }) => {
  const [items, setItems] = useState<MediaItem[]>([]);
  const [activeCategory, setActiveCategory] = useState<'all' | MediaItem['category']>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadMode, setUploadMode] = useState<'file' | 'url'>('file');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState('');
  const [calculatedSize, setCalculatedSize] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newCategory, setNewCategory] = useState<MediaItem['category']>('photo');
  const [newUrl, setNewUrl] = useState('');
  const [newThumb, setNewThumb] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadItems = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/media', { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load media library');
      const data = await res.json();
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load media library');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadItems();
  }, []);

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      const matchCat = activeCategory === 'all' || item.category === activeCategory;
      const q = searchQuery.trim().toLowerCase();
      const matchSearch = !q || item.title.toLowerCase().includes(q) || item.description.toLowerCase().includes(q);
      return matchCat && matchSearch;
    });
  }, [items, activeCategory, searchQuery]);

  const resetUploadState = () => {
    setSelectedFile(null);
    setFilePreview('');
    setCalculatedSize('');
    setNewTitle('');
    setNewDesc('');
    setNewUrl('');
    setNewThumb('');
    setNewCategory('photo');
    setUploadMode('file');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);

    const sizeInMB = file.size / (1024 * 1024);
    setCalculatedSize(sizeInMB >= 1 ? `${sizeInMB.toFixed(1)} MB` : `${Math.round(file.size / 1024)} KB`);

    if (file.type.startsWith('image/')) setNewCategory('photo');
    else if (file.type.startsWith('video/')) setNewCategory('video');
    else if (file.type.includes('pdf')) setNewCategory('tactics');
    else setNewCategory('document');

    if (!newTitle) {
      const cleanName = file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');
      setNewTitle(cleanName);
    }

    if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
      const reader = new FileReader();
      reader.onloadend = () => setFilePreview(String(reader.result || ''));
      reader.readAsDataURL(file);
    } else {
      setFilePreview('');
    }
  };

  const handleUploadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isOrganizer) return;
    setSubmitting(true);
    setActionError('');

    try {
      if (uploadMode === 'file') {
        if (!selectedFile) throw new Error('Please select a file first');
        const form = new FormData();
        form.append('file', selectedFile);
        form.append('title', newTitle.trim() || selectedFile.name);
        form.append('description', newDesc.trim());
        form.append('category', newCategory);
        const res = await fetch('/api/media/upload', {
          method: 'POST',
          body: form,
          credentials: 'include',
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Upload failed');
      } else {
        const res = await fetch('/api/media/link', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: newTitle.trim(),
            description: newDesc.trim(),
            category: newCategory,
            fileUrl: newUrl.trim(),
            thumbnailUrl: newThumb.trim(),
            fileSize: calculatedSize.trim(),
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'URL publish failed');
      }

      resetUploadState();
      setShowUploadModal(false);
      await loadItems();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = async (item: MediaItem) => {
    setActionError('');
    try {
      const res = await fetch(`/api/media/${item.id}/download`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.item) {
        setItems((prev) => prev.map((entry) => (entry.id === item.id ? data.item : entry)));
      }
    } catch {
      // Best-effort only; do not block download.
    }

    const link = document.createElement('a');
    link.href = item.fileUrl;
    link.target = '_blank';
    link.rel = 'noreferrer';
    link.download = item.title.replace(/\s+/g, '_');
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleDelete = async (item: MediaItem) => {
    if (!confirm(`Remove “${item.title}” from the media library?`)) return;
    setActionError('');
    try {
      const res = await fetch(`/api/media/${item.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Delete failed');
      setItems((prev) => prev.filter((entry) => entry.id !== item.id));
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6 pb-20">
      <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl flex flex-wrap items-center justify-between gap-4 shadow-2xl">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/40 rounded-2xl text-emerald-400">
            <Sparkles className="w-7 h-7" />
          </div>
          <div>
            <h2 className="text-2xl font-black text-white flex items-center gap-2">
              Media Hub & Tactical Library
              <span className="text-[10px] font-mono bg-emerald-500/15 text-emerald-400 px-2.5 py-0.5 rounded-full border border-emerald-500/30 uppercase">
                Persistent VPS Storage
              </span>
            </h2>
            <p className="text-xs text-slate-400">
              Photos, videos, PDFs, and files are now meant to stay on the VPS after refresh and reload.
            </p>
          </div>
        </div>

        {isOrganizer && (
          <button
            onClick={() => setShowUploadModal(true)}
            className="px-5 py-3 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs rounded-2xl transition shadow-lg shadow-emerald-500/20 flex items-center gap-2"
          >
            <Plus className="w-4 h-4 stroke-[3]" /> Share Media / File
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 bg-slate-900/80 p-3 rounded-2xl border border-slate-800">
        <div className="flex gap-1.5 overflow-x-auto no-scrollbar text-xs font-bold font-mono">
          {([
            { id: 'all', label: 'All Media', icon: Sparkles },
            { id: 'photo', label: 'Photos', icon: ImageIcon },
            { id: 'video', label: 'Match Highlights', icon: Video },
            { id: 'tactics', label: 'Tactics & Playbooks', icon: FileText },
            { id: 'document', label: 'Documents & Files', icon: Download },
          ] as const).map((cat) => {
            const Icon = cat.icon;
            const isActive = activeCategory === cat.id;
            return (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id as 'all' | MediaItem['category'])}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl transition ${
                  isActive
                    ? 'bg-emerald-500 text-slate-950 font-black shadow-md shadow-emerald-500/20'
                    : 'bg-slate-950/80 text-slate-400 hover:text-white border border-slate-800'
                }`}
              >
                <Icon className="w-3.5 h-3.5 shrink-0" />
                <span>{cat.label}</span>
              </button>
            );
          })}
        </div>

        <div className="relative w-full sm:w-64">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
          <input
            type="text"
            placeholder="Search photos, clips, playbooks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-slate-950 border border-slate-800 text-white text-xs rounded-xl pl-9 p-2.5 focus:outline-none focus:border-emerald-500"
          />
        </div>
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded-2xl p-4">{error}</div>}
      {actionError && <div className="bg-amber-500/10 border border-amber-500/30 text-amber-200 text-sm rounded-2xl p-4">{actionError}</div>}

      {loading ? (
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-8 text-center text-slate-400">Loading media library…</div>
      ) : filteredItems.length === 0 ? (
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-8 text-center text-slate-400">
          No media found here yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredItems.map((item) => {
            const canDelete = isOrganizer && (item.ownerUserId === activeMember.userId || ['owner', 'admin'].includes(activeMember.role));
            return (
              <div key={item.id} className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden shadow-xl flex flex-col justify-between">
                <div>
                  <div className="relative aspect-video bg-slate-950 overflow-hidden border-b border-slate-800 group">
                    {item.category === 'video' ? (
                      <div className="w-full h-full relative flex items-center justify-center">
                        {item.thumbnailUrl ? (
                          <img src={item.thumbnailUrl} alt={item.title} className="w-full h-full object-cover opacity-80" />
                        ) : (
                          <div className="absolute inset-0 bg-slate-950" />
                        )}
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                          <a
                            href={item.fileUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="w-14 h-14 rounded-full bg-emerald-500 text-slate-950 flex items-center justify-center shadow-2xl hover:scale-110 transition"
                          >
                            <Video className="w-7 h-7 fill-slate-950" />
                          </a>
                        </div>
                      </div>
                    ) : item.category === 'photo' ? (
                      <img src={item.thumbnailUrl || item.fileUrl} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-slate-950 to-slate-900 p-6 flex flex-col items-center justify-center text-center space-y-3">
                        <div className="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400">
                          <FileText className="w-10 h-10" />
                        </div>
                        <span className="text-xs font-mono font-extrabold uppercase text-amber-300">
                          {item.category.toUpperCase()} • {item.fileSize || 'FILE'}
                        </span>
                      </div>
                    )}

                    <div className="absolute top-3 left-3 bg-slate-950/90 backdrop-blur-md px-3 py-1 rounded-full text-[10px] font-mono font-bold text-emerald-400 border border-emerald-500/30 uppercase">
                      {item.category}
                    </div>
                    <div className="absolute top-3 right-3 bg-slate-950/90 backdrop-blur-md px-3 py-1 rounded-full text-[10px] font-mono text-slate-300 border border-slate-800">
                      {item.fileSize || 'FILE'}
                    </div>
                  </div>

                  <div className="p-5 space-y-3">
                    <div className="flex items-center justify-between text-[11px] text-slate-400 font-mono gap-3">
                      <span className="truncate">By <strong className="text-white">{item.authorName}</strong> ({item.authorRole})</span>
                      <span className="shrink-0">{formatWhen(item.createdAt)}</span>
                    </div>
                    <h3 className="font-extrabold text-base text-white leading-snug">{item.title}</h3>
                    <p className="text-xs text-slate-300 leading-relaxed">{item.description}</p>
                  </div>
                </div>

                <div className="p-5 border-t border-slate-800 bg-slate-950/60 space-y-3">
                  <div className="flex items-center justify-between text-[11px] text-slate-400 font-mono">
                    <span>{categoryLabel[item.category]}</span>
                    <span>{item.downloadsCount} download{item.downloadsCount === 1 ? '' : 's'}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => handleDownload(item)}
                      className="px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-extrabold text-xs rounded-xl shadow-lg shadow-emerald-500/20 flex items-center gap-1.5 transition"
                    >
                      <Download className="w-4 h-4 stroke-[2.5]" /> Download
                    </button>
                    <a
                      href={item.fileUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs rounded-xl flex items-center gap-1.5 transition"
                    >
                      <ExternalLink className="w-4 h-4" /> Open
                    </a>
                    {canDelete && (
                      <button
                        onClick={() => handleDelete(item)}
                        className="px-4 py-2 bg-red-500/15 hover:bg-red-500/25 text-red-300 border border-red-500/30 font-bold text-xs rounded-xl flex items-center gap-1.5 transition"
                      >
                        <Trash2 className="w-4 h-4" /> Remove
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showUploadModal && isOrganizer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
          <div className="bg-slate-900 border border-slate-800 w-full max-w-lg rounded-3xl p-6 space-y-4 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 className="font-black text-lg text-white flex items-center gap-2">
                <Upload className="w-5 h-5 text-emerald-400" /> Share Media or File
              </h3>
              <button onClick={() => setShowUploadModal(false)} className="text-slate-400 hover:text-white text-xs font-bold p-1">
                ✕
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2 p-1 bg-slate-950 rounded-xl border border-slate-800 text-xs font-bold">
              <button
                type="button"
                onClick={() => setUploadMode('file')}
                className={`py-2 rounded-lg transition ${uploadMode === 'file' ? 'bg-emerald-500 text-slate-950 font-black' : 'text-slate-400 hover:text-white'}`}
              >
                📁 Direct File Upload
              </button>
              <button
                type="button"
                onClick={() => setUploadMode('url')}
                className={`py-2 rounded-lg transition ${uploadMode === 'url' ? 'bg-emerald-500 text-slate-950 font-black' : 'text-slate-400 hover:text-white'}`}
              >
                🔗 Paste External URL
              </button>
            </div>

            <form onSubmit={handleUploadSubmit} className="space-y-4 text-xs">
              {uploadMode === 'file' ? (
                <div>
                  <label className="block text-slate-300 font-bold mb-1.5">Select File from Phone / Device</label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.zip,.txt"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-emerald-500/40 hover:border-emerald-500 bg-slate-950/80 rounded-2xl p-5 text-center cursor-pointer transition flex flex-col items-center justify-center gap-2 group"
                  >
                    {filePreview ? (
                      <div className="space-y-2 w-full">
                        {newCategory === 'photo' ? (
                          <img src={filePreview} alt="Preview" className="max-h-40 mx-auto rounded-xl object-contain border border-slate-800" />
                        ) : newCategory === 'video' ? (
                          <video src={filePreview} controls className="max-h-40 mx-auto rounded-xl border border-slate-800" />
                        ) : (
                          <div className="p-4 bg-emerald-500/10 text-emerald-400 rounded-xl inline-block font-mono font-extrabold">
                            📄 {selectedFile?.name}
                          </div>
                        )}
                        <p className="text-[11px] text-emerald-400 font-bold">✓ File selected: {selectedFile?.name} ({calculatedSize})</p>
                        <p className="text-[10px] text-slate-400">Tap to change file</p>
                      </div>
                    ) : selectedFile ? (
                      <div className="space-y-2 w-full">
                        <div className="p-4 bg-emerald-500/10 text-emerald-400 rounded-xl inline-block font-mono font-extrabold">
                          📄 {selectedFile.name}
                        </div>
                        <p className="text-[11px] text-emerald-400 font-bold">✓ File selected: {selectedFile.name} ({calculatedSize})</p>
                        <p className="text-[10px] text-slate-400">Tap to change file</p>
                      </div>
                    ) : (
                      <>
                        <div className="p-3 bg-emerald-500/10 text-emerald-400 rounded-2xl border border-emerald-500/30 group-hover:scale-110 transition">
                          <Upload className="w-6 h-6" />
                        </div>
                        <div>
                          <p className="font-extrabold text-white text-xs">Tap to browse gallery or files</p>
                          <p className="text-[10px] text-slate-400 mt-0.5">Supports photos, match videos, PDFs, docs and common files</p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="block text-slate-300 font-bold mb-1">Direct Downloadable URL</label>
                    <input
                      type="url"
                      required
                      value={newUrl}
                      onChange={(e) => setNewUrl(e.target.value)}
                      placeholder="https://example.com/file.mp4"
                      className="w-full bg-slate-950 border border-slate-800 text-slate-200 p-2.5 rounded-xl font-mono text-[11px]"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-300 font-bold mb-1">Thumbnail URL (optional)</label>
                    <input
                      type="url"
                      value={newThumb}
                      onChange={(e) => setNewThumb(e.target.value)}
                      placeholder="https://example.com/thumb.jpg"
                      className="w-full bg-slate-950 border border-slate-800 text-slate-200 p-2.5 rounded-xl font-mono text-[11px]"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-slate-300 font-bold mb-1">Title</label>
                <input
                  type="text"
                  required
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="e.g. Winner Goal Celebration / Matchday Squad Photo"
                  className="w-full bg-slate-950 border border-slate-800 text-white p-2.5 rounded-xl"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-300 font-bold mb-1">Media Category</label>
                  <select
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value as MediaItem['category'])}
                    className="w-full bg-slate-950 border border-slate-800 text-white p-2.5 rounded-xl font-bold"
                  >
                    <option value="photo">Photo / Image</option>
                    <option value="video">Highlight Video Clip</option>
                    <option value="tactics">Tactical Playbook (PDF)</option>
                    <option value="document">Document / File</option>
                  </select>
                </div>
                <div>
                  <label className="block text-slate-300 font-bold mb-1">File Size Tag</label>
                  <input
                    type="text"
                    value={calculatedSize}
                    onChange={(e) => setCalculatedSize(e.target.value)}
                    placeholder="e.g. 5.2 MB"
                    className="w-full bg-slate-950 border border-slate-800 text-slate-300 p-2.5 rounded-xl font-mono text-[11px]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-300 font-bold mb-1">Description (Optional)</label>
                <textarea
                  rows={2}
                  value={newDesc}
                  onChange={(e) => setNewDesc(e.target.value)}
                  placeholder="Brief summary of what this media file includes..."
                  className="w-full bg-slate-950 border border-slate-800 text-white p-2.5 rounded-xl"
                />
              </div>

              {actionError && <div className="bg-red-500/10 border border-red-500/30 text-red-300 rounded-xl p-3">{actionError}</div>}

              <div className="flex gap-2 pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 py-3 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-slate-950 font-black text-xs rounded-xl shadow transition"
                >
                  {submitting ? 'Publishing…' : 'Publish to Squad Media 🚀'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="px-4 py-3 bg-slate-800 text-slate-300 font-bold text-xs rounded-xl"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
